"use client";

import { useNavigate } from "@tanstack/react-router";
import { Loader, Store } from "lucide-react";
import { useEffect } from "react";
import type { ShopVerifiedLevel } from "../api/client";
import { VERIFIED_LEVEL_LABELS } from "../lib/shop-verification";
import { useMerchantShops } from "../store/use-merchant-shops";

/** หน้าแรกหลัง login — ถ้ามีร้านเดียวเข้าไปเลย, หลายร้านแสดงรายการให้เลือก */
export function MyShopsPage() {
  const { shops, isLoading, error } = useMerchantShops();
  const navigate = useNavigate();

  /** มีร้านเดียว → redirect เข้าร้านนั้นเลย ไม่ต้องกดเลือก */
  useEffect(() => {
    if (isLoading || error || shops.length !== 1) return;
    navigate({
      to: "/app/shops/$shopId",
      params: { shopId: shops[0]!.shopId },
    });
  }, [isLoading, error, shops, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-8">
        <p className="text-destructive">ເກີດຂໍ້ຜິດພາດ: {error.message}</p>
      </div>
    );
  }

  if (shops.length === 0) {
    return (
      <div className="container py-8">
        <h1 className="mb-4 font-semibold text-xl">ລາຍການລາຍ</h1>
        <p className="text-muted-foreground">ທ່ານຍັງບໍ່ໄດ້ຖືກມອບໃຫ້ລາຍການໃດເທື່ອ.</p>
      </div>
    );
  }

  /* มีร้านเดียว — กำลัง redirect เข้าร้านนั้น (ไม่แสดงรายการให้กด) */
  if (shops.length === 1) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container py-8">
      <h1 className="mb-6 font-semibold text-xl">ລາຍການລາຍ</h1>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shops.map((shop) => (
          <button
            key={shop.shopId}
            type="button"
            className="flex items-start gap-4 rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent"
            onClick={() =>
              navigate({
                to: "/app/shops/$shopId",
                params: { shopId: shop.shopId },
              })
            }
          >
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Store className="size-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium">{shop.shopName}</p>
              <p className="text-muted-foreground text-sm">
                {shop.branchName ?? "ສາຍການຫຼັກ"} · {shop.roleName}
              </p>
              {shop.verifiedLevel && (
                <span
                  className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-muted-foreground text-xs"
                  title={
                    VERIFIED_LEVEL_LABELS[
                      shop.verifiedLevel as ShopVerifiedLevel
                    ]
                  }
                >
                  {
                    VERIFIED_LEVEL_LABELS[
                      shop.verifiedLevel as ShopVerifiedLevel
                    ]
                  }
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
